import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import ConfirmStatementDeletionDialog from 'modules/common/components/VerifiedStatementUpload/components/ConfirmStatementDeletionDialog';
import StatementPeriodMenu from 'modules/common/components/VerifiedStatementUpload/components/StatementPeriodMenu';
import { formatPeriod } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import { isBankDelivered } from 'modules/common/components/VerifiedStatementUpload/utils/heldHereFiles';
import { periodKey, rowKey, sortPeriods } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import {
    FileHoldings,
    removalDeletesFile,
    removeControlLabel,
} from 'modules/common/components/VerifiedStatementUpload/utils/removalOutcome';
import { PeriodOwner } from 'modules/common/components/VerifiedStatementUpload/utils/resolveStatementAssignments';
import routeStatementRow from 'modules/common/components/VerifiedStatementUpload/utils/routeStatementRow';
import siblingMonthsByRow, {
    offerableMonths,
} from 'modules/common/components/VerifiedStatementUpload/utils/siblingMonthsByRow';
import { accountMaskLabel } from 'modules/common/components/VerifiedStatementUpload/utils/statementFilings';
import {
    hasProcessingExpired,
    ResolvedStatementVerdict,
} from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import { AsyncRowIconButton } from 'modules/common/components/RowIconButton';
import StatusRow from 'modules/common/components/StatusRow';
import { HeldStatement, StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

export interface StatementFile {
    documentUuid: string;
    fileName: string;
    /** ISO upload time, used to age out documents whose processing job never appeared. */
    uploadedAt?: string;
}

/** A removal that deletes the file, held until the member confirms it. */
interface PendingDeletion {
    readonly documentUuid: string;
    readonly fileName: string;
    /** The month the row shows, which the confirmation names. Absent on a row standing for the file. */
    readonly shownPeriod?: StatementPeriod;
    /** The tracker row the removal takes — see `RowControlsProps.removePeriod`. */
    readonly removePeriod?: StatementPeriod;
}

interface RowControlsProps {
    readonly documentUuid: string;
    readonly fileName: string;
    /** This card's mask, for the remove control's name. */
    readonly accountMask: string;
    /** The months this row's menu offers — required, minus any a sibling row already holds. */
    readonly periods: StatementPeriod[];
    /** The month this row's file currently holds, if any. */
    readonly selectedPeriod?: StatementPeriod;
    /**
     * The tracker row a correction here starts from.
     *
     * Bound in here rather than handed to the menu, so the picker keeps knowing only about
     * documents and months: which row is being edited is the row's own business.
     */
    readonly sourcePeriod?: StatementPeriod;
    readonly isAssigned: boolean;
    /** False where naming a month would credit one on no evidence. */
    readonly canAssignPeriod: boolean;
    /** Appended to the month menu's accessible name — see `StatementPeriodMenu.labelSuffix`. */
    readonly menuLabelSuffix?: string;
    /** The month the row shows, which the remove control names. Absent on a row standing for the file. */
    readonly shownPeriod?: StatementPeriod;
    /** The month the remove control takes off this card. Absent: every month the file holds here. */
    readonly removePeriod?: StatementPeriod;
    /** The removal is the file's last hold anywhere, so it deletes the file and asks first. */
    readonly deletesFile: boolean;
    /** Another row's removal is in flight, and this row's label may be about to change. */
    readonly isRemoveDisabled: boolean;
    readonly onRemove: (documentUuid: string, period?: StatementPeriod) => void | Promise<void>;
    readonly onConfirmDeletion: (deletion: PendingDeletion) => void;
    readonly onSelectPeriod: (documentUuid: string, period: StatementPeriod, sourcePeriod?: StatementPeriod) => void;
    readonly onClearPeriod: (documentUuid: string, sourcePeriod?: StatementPeriod) => void;
}

/**
 * A row's trailing controls: the month menu where it applies, then remove.
 *
 * Owns the menu rather than taking it as a node, because five of its seven props are the same
 * on both kinds of row and threading them through twice is how the two drift apart.
 */
function RowControls({
    documentUuid,
    fileName,
    accountMask,
    periods,
    selectedPeriod = undefined,
    sourcePeriod = undefined,
    isAssigned,
    canAssignPeriod,
    menuLabelSuffix = '',
    shownPeriod = undefined,
    removePeriod = undefined,
    deletesFile,
    isRemoveDisabled,
    onRemove,
    onConfirmDeletion,
    onSelectPeriod,
    onClearPeriod,
}: Readonly<RowControlsProps>) {
    const handleSelect = useCallback(
        (uuid: string, period: StatementPeriod) => onSelectPeriod(uuid, period, sourcePeriod),
        [onSelectPeriod, sourcePeriod]
    );
    const handleClear = useCallback((uuid: string) => onClearPeriod(uuid, sourcePeriod), [onClearPeriod, sourcePeriod]);

    return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
            {canAssignPeriod && (
                <StatementPeriodMenu
                    documentUuid={documentUuid}
                    periods={periods}
                    selectedPeriod={selectedPeriod}
                    isAssigned={isAssigned}
                    onSelect={handleSelect}
                    onClear={handleClear}
                    fileName={fileName}
                    labelSuffix={menuLabelSuffix}
                />
            )}
            <AsyncRowIconButton
                icon={<CloseXIcon />}
                label={removeControlLabel({ fileName, period: shownPeriod, accountMask, deletesFile })}
                onClick={() =>
                    deletesFile
                        ? onConfirmDeletion({ documentUuid, fileName, shownPeriod, removePeriod })
                        : onRemove(documentUuid, removePeriod)
                }
                disabled={isRemoveDisabled}
                // The label says what the ✕ removes, which its icon alone can't.
                showLabelTooltip
            />
        </Stack>
    );
}

interface StatementSlotListProps {
    readonly files: StatementFile[];
    readonly verdictByUuid: Record<string, ResolvedStatementVerdict>;
    /**
     * Which document owns each required month, from the resolver. Passed in rather than
     * inverted from the verdicts here: a month has at most one owner, and deciding who that is
     * belongs to the one place that already decides it.
     */
    readonly ownerByPeriodKey: Map<string, PeriodOwner>;
    /** Where each file holds rows, so a remove control can say when it deletes the file. */
    readonly holdingsByUuid: Record<string, FileHoldings>;
    /** This card's mask, for the remove controls' names. */
    readonly accountMask: string;
    /** Take a file off this card: one month, or every month it holds here when the period is absent. */
    readonly onRemove: (documentUuid: string, period?: StatementPeriod) => void | Promise<void>;
    /** Every month this account was asked for — one row each, filled or not. */
    readonly requiredPeriods: StatementPeriod[];
    /** This card's held months, from coverage, for the slots no listed file fills. */
    readonly heldPeriods?: HeldStatement[];
    readonly onSelectPeriod: (documentUuid: string, period: StatementPeriod, sourcePeriod?: StatementPeriod) => void;
    readonly onClearPeriod: (documentUuid: string, sourcePeriod?: StatementPeriod) => void;
}

/**
 * What a slot no listed file fills says. A listed holder means a staged edit moved it off, so it
 * previews Save as "Missing"; any other holder (the bank, an unnamed upload) is real coverage.
 */
function unfilledSlot(
    held: HeldStatement | undefined,
    listed: ReadonlyMap<string, StatementFile>
): { tone: 'success' | 'neutral'; subtitle: string } {
    if (!held || (held.member_document_uuid && listed.has(held.member_document_uuid))) {
        return { tone: 'neutral', subtitle: 'Missing' };
    }
    return { tone: 'success', subtitle: isBankDelivered(held) ? 'Received from your bank' : 'Statement on file' };
}

/**
 * The statement upload modal's two lists: files we couldn't place, then a row per month asked
 * for.
 *
 * The bottom section is keyed by **period**, not by file, and that is the whole difference
 * from the file-per-row list it replaces. A month nobody has covered still gets a row — "March
 * 2026 / Missing" — so the ask is legible before anything is uploaded and stays legible as it
 * fills in. No file-shaped row component can express that row, because it has no file behind
 * it and therefore no document UUID to key on.
 *
 * Files still in flight appear in neither section. The dropzone above counts them in
 * "Processing N files…", which is the one place a count is honest while verdicts are unknown —
 * a row per in-flight file would have to claim a month or a failure, and it has neither yet.
 */
export default function StatementSlotList({
    files,
    verdictByUuid,
    ownerByPeriodKey,
    holdingsByUuid,
    accountMask,
    onRemove,
    requiredPeriods,
    heldPeriods = undefined,
    onSelectPeriod,
    onClearPeriod,
}: Readonly<StatementSlotListProps>) {
    /**
     * A removal is in flight. Every remove control waits it out: each one's label says whether it
     * deletes the file, read from what the file still holds, and that is only true again once the
     * removal has landed and the reads behind the labels refetched.
     */
    const [isRemoveDisabled, setIsRemoveDisabled] = useState(false);
    const handleRemove = useCallback(
        async (documentUuid: string, period?: StatementPeriod) => {
            setIsRemoveDisabled(true);
            try {
                await onRemove(documentUuid, period);
            } finally {
                setIsRemoveDisabled(false);
            }
        },
        [onRemove]
    );

    const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
    const handleCancelDeletion = useCallback(() => setPendingDeletion(null), []);
    const handleConfirmDeletion = useCallback(
        (deletion: PendingDeletion) =>
            handleRemove(deletion.documentUuid, deletion.removePeriod)
                // The dialog's button discards this promise, as a row control's would. The write
                // says what failed itself where it can; this keeps any other rejection from vanishing.
                .catch((error: unknown) =>
                    sendDatadogRumError('Statement deletion failed', error, { source: 'StatementSlotList' })
                )
                .finally(() => setPendingDeletion(null)),
        [handleRemove]
    );

    // Deliberately not memoised: `hasProcessingExpired` reads the clock, and a memo keyed on
    // files and verdicts would freeze that reading exactly when it matters. Once the poll gives
    // up, neither dependency ever changes again, so a memoised expiry would never be observed —
    // the row telling the member to delete a stuck file would only appear if they reopened the
    // modal. Cheap to redo: at most `MAX_FILES` entries.
    //
    // Not memoising is only half of it: something still has to render at the moment the clock
    // crosses the boundary, and by then the poll has stopped and nothing else is changing. The
    // parent schedules that render — `useProcessingExpiryTick` in `VerifiedStatementUploadModal`
    // — and this list re-renders with it. Memoising this component would cut that off, so it
    // takes its own tick if it ever gains one.
    const placements = files.map((file) => {
        const verdict = verdictByUuid[file.documentUuid];
        return {
            file,
            verdict,
            placement: routeStatementRow(verdict, hasProcessingExpired(verdict, file.uploadedAt), requiredPeriods),
        };
    });

    const unmatched = placements.filter(({ placement }) => placement.destination === 'unmatched');

    /**
     * The file behind each owned month.
     *
     * A lookup, not a decision — the resolver already picked one owner per month, and every
     * `matched` verdict owns at least one required month by construction, so a file routed to
     * a slot always has one. A multi-month document appears in each month it owns, which is
     * the point of per-month rows.
     */
    const fileByUuid = useMemo(() => new Map(files.map((file) => [file.documentUuid, file])), [files]);

    const slots = useMemo(() => sortPeriods(requiredPeriods), [requiredPeriods]);

    /** Coverage's holder per month, for the slots no listed file fills. */
    const heldByPeriodKey = useMemo(
        () => new Map((heldPeriods ?? []).map((held) => [periodKey(held), held])),
        [heldPeriods]
    );

    /** The one slot each file's notes sit under — its first month — so they aren't repeated per month. */
    const notedSlotByUuid = useMemo(() => {
        const noted = new Map<string, string>();
        slots.forEach((period) => {
            const owner = ownerByPeriodKey.get(periodKey(period));
            if (owner && !noted.has(owner.documentUuid)) {
                noted.set(owner.documentUuid, periodKey(period));
            }
        });
        return noted;
    }, [slots, ownerByPeriodKey]);

    /**
     * Per row, the months its siblings are on — the months its menu must not offer.
     *
     * Memoised on the two props it reads and nothing else. The clock is not one of them, so this
     * is safe to freeze in a way the placement pass above is not.
     */
    const siblingMonths = useMemo(
        () => siblingMonthsByRow(verdictByUuid, ownerByPeriodKey),
        [verdictByUuid, ownerByPeriodKey]
    );

    return (
        <Stack spacing={3} sx={{ width: '100%' }}>
            {unmatched.length > 0 && (
                <Stack spacing={1}>
                    <Typography variant="bodyReducedSemiBold" color="text.primary">
                        {`Files we couldn't match (${unmatched.length})`}
                    </Typography>
                    {unmatched.map(({ file, verdict, placement }) => {
                        const deletesFile = removalDeletesFile(holdingsByUuid[file.documentUuid]);
                        // The row a correction here moves: the file's first, since a file in this
                        // list owns no month and so has no row the member singled out. Deterministic
                        // rather than withheld — `routeStatementRow` guarantees the menu on every row
                        // that renders, so a file is always rescuable.
                        const addressableRow = verdict?.heldRows[0];

                        return (
                            <StatusRow
                                key={file.documentUuid}
                                tone={placement.tone}
                                title={file.fileName}
                                subtitle={placement.message}
                                notes={verdict?.notes}
                                actions={
                                    <RowControls
                                        documentUuid={file.documentUuid}
                                        fileName={file.fileName}
                                        accountMask={accountMask}
                                        // A displaced multi-row file is offered fewer, on the
                                        // same rule as a slot row: the month a sibling row of it
                                        // still holds would be refused by the fold, so offering
                                        // it is a click that does nothing.
                                        periods={offerableMonths(
                                            requiredPeriods,
                                            siblingMonths.get(rowKey(file.documentUuid, addressableRow))
                                        )}
                                        selectedPeriod={verdict?.assignedPeriod}
                                        sourcePeriod={addressableRow}
                                        isAssigned={Boolean(verdict?.assignedPeriod)}
                                        // False only for an expired file, which nothing ever read —
                                        // see the escape-hatch invariant in `routeStatementRow`.
                                        canAssignPeriod={placement.canAssignPeriod}
                                        // No `shownPeriod` or `removePeriod`: this row stands for
                                        // the file, so its remove names no month and takes
                                        // everything the file holds on this card.
                                        deletesFile={deletesFile}
                                        isRemoveDisabled={isRemoveDisabled}
                                        onRemove={handleRemove}
                                        onConfirmDeletion={setPendingDeletion}
                                        onSelectPeriod={onSelectPeriod}
                                        onClearPeriod={onClearPeriod}
                                    />
                                }
                            />
                        );
                    })}
                </Stack>
            )}

            {slots.length > 0 && (
                <Stack spacing={1}>
                    <Typography variant="bodyReducedSemiBold" color="text.primary">
                        {slots.length === 1 ? 'Statement' : 'Statements'}
                    </Typography>
                    {slots.map((period) => {
                        const ownership = ownerByPeriodKey.get(periodKey(period));
                        const owner = ownership ? fileByUuid.get(ownership.documentUuid) : undefined;
                        const label = formatPeriod(period);
                        // One document can fill several month rows. Where it does, each row's
                        // month menu has to name the month it sits on: the file is the same one on
                        // every row, so its name alone would read as three different files.
                        //
                        // Counted from the months it occupies HERE, not from its tracker rows. A
                        // file coverage has not attributed rows to yet still draws a row per month
                        // we read on it, and counted off the rows every one of those controls was
                        // named identically for as long as that window lasted.
                        const ownerVerdict = owner ? verdictByUuid[owner.documentUuid] : undefined;
                        const spansMonths = (ownerVerdict?.coveredPeriods.length ?? 0) > 1;
                        const selectable = offerableMonths(
                            requiredPeriods,
                            ownership && siblingMonths.get(rowKey(ownership.documentUuid, ownership.sourcePeriod))
                        );
                        // Named only where the file was uploaded under a different card: on its own
                        // card it is simply this card's statement.
                        const uploadedUnder = ownerVerdict?.uploadedUnderMask
                            ? ` · uploaded under ${accountMaskLabel(ownerVerdict.uploadedUnderMask)}`
                            : '';
                        const unfilled = owner
                            ? undefined
                            : unfilledSlot(heldByPeriodKey.get(periodKey(period)), fileByUuid);
                        // The server-side row, not the slot's month, which a staged edit may have
                        // moved. Falls back to the slot's month rather than to none, because a
                        // removal naming no month takes every month the file holds on this card.
                        // An owner has no row when the statuses read runs ahead of coverage (the
                        // slot's month is then the row's) or when it is a pick on a file holding
                        // none (nothing here to take, which the server treats as a no-op).
                        const removePeriod = ownership?.sourcePeriod ?? period;
                        const deletesFile =
                            owner !== undefined && removalDeletesFile(holdingsByUuid[owner.documentUuid], removePeriod);

                        return (
                            <StatusRow
                                key={periodKey(period)}
                                tone={unfilled?.tone ?? 'success'}
                                title={label}
                                subtitle={owner ? `Matched · ${owner.fileName}${uploadedUnder}` : unfilled?.subtitle}
                                notes={
                                    owner && notedSlotByUuid.get(owner.documentUuid) === periodKey(period)
                                        ? ownerVerdict?.notes
                                        : undefined
                                }
                                actions={
                                    owner && (
                                        <RowControls
                                            documentUuid={owner.documentUuid}
                                            fileName={owner.fileName}
                                            accountMask={accountMask}
                                            periods={selectable}
                                            // Always this row's month. A correction is scoped to
                                            // the row it is made on, so picking the ticked month
                                            // is a genuine no-op — it no longer collapses a
                                            // multi-month document onto the month picked.
                                            selectedPeriod={period}
                                            // Optional despite `owner` proving `ownership` is
                                            // set — `owner` is derived from it, which tsc cannot
                                            // follow, so narrowing here needs the chain.
                                            sourcePeriod={ownership?.sourcePeriod}
                                            isAssigned={Boolean(ownership?.isAssigned)}
                                            canAssignPeriod
                                            shownPeriod={period}
                                            removePeriod={removePeriod}
                                            deletesFile={deletesFile}
                                            isRemoveDisabled={isRemoveDisabled}
                                            onRemove={handleRemove}
                                            onConfirmDeletion={setPendingDeletion}
                                            onSelectPeriod={onSelectPeriod}
                                            onClearPeriod={onClearPeriod}
                                            menuLabelSuffix={spansMonths ? ` (matched to ${label})` : ''}
                                        />
                                    )
                                }
                            />
                        );
                    })}
                </Stack>
            )}

            {pendingDeletion && (
                <ConfirmStatementDeletionDialog
                    open
                    onClose={handleCancelDeletion}
                    onConfirm={() => handleConfirmDeletion(pendingDeletion)}
                    fileName={pendingDeletion.fileName}
                    period={pendingDeletion.shownPeriod}
                    accountMask={accountMask}
                    isRemoving={isRemoveDisabled}
                />
            )}
        </Stack>
    );
}
